/**
 * WonderPages Personalisierungs-Funnel — Core JS
 *
 * Sprint 1: State, Step-Nav, Step 1 (Name/Geschlecht/Haar/Augen)
 * Sprint 2: Cloudinary-Upload (Step 2)
 * Sprint 3: Loading-Stub (Step 3) + Charakter-Preview (Step 4)
 * Sprint 4: Binding (Step 5) + Extras (Step 6) + Live-Preis im Weiter-Button
 * Sprint 5: Upsell-Mocks (Step 7) + Ajax-Cart-Submit
 * Sprint 6: PDP/Funnel-Modus-Trennung + Visual-Polish (Swatches, Eye-Icons, Emojis)
 *
 * Modus-Logik:
 *   Default = PDP-Mode (Hero + CTA "Personalisieren").
 *   Mit ?view=funnel = Funnel-Mode (Full-Screen-Funnel über 7 Steps).
 *   Klasse `.is-funnel-mode` wird auf <html> gesetzt — CSS regelt Visibility.
 *
 * State lebt in sessionStorage unter Key "wp_funnel".
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'wp_funnel';
  const TOTAL_STEPS = 7;

  // ---------- Cloudinary-Config ----------
  const CLOUDINARY = {
    cloudName: 'dolemb1ce',
    uploadPreset: 'wonderpages_photos',
    endpoint: 'https://api.cloudinary.com/v1_1/dolemb1ce/image/upload',
    maxBytes: 10 * 1024 * 1024,
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  };

  // ---------- Step-3 Loading-Config ----------
  const LOADING_DURATION_MS = 30000;
  const LOADING_SUBSTEPS = [
    { from: 0,  label: 'Dein Abenteuer wird zusammengestellt' },
    { from: 25, label: 'Dein Name wird eingefügt' },
    { from: 50, label: 'Die Figur wird gestaltet' },
    { from: 75, label: 'Der letzte Schliff' }
  ];

  // ---------- Step-5 Binding-Config ----------
  const BINDINGS = [
    { code: 'SC', skuSuffix: '-SC', label: 'Softcover', desc: 'Leicht und flexibel — der günstige Einstieg.' },
    { code: 'HC', skuSuffix: '-HC', label: 'Hardcover', desc: 'Robuster Einband — fühlt sich nach echtem Buch an.', recommended: true, isDefault: true },
    { code: 'LF', skuSuffix: '-LF', label: 'Layflat',    desc: 'Liegt komplett flach auf — Foto-Buch-Feeling, Premium.' }
  ];

  // ---------- Step-7 Upsell-Mocks ----------
  const UPSELL_MOCKS = [
    { icon: '🚀', title: 'Weltraum-Abenteuer', desc: 'Reise bis zu den Sternen und zurück.' },
    { icon: '👑', title: 'Königreich-Abenteuer', desc: 'Ein Abenteuer im verzauberten Schloss.' },
    { icon: '🏴‍☠️', title: 'Piraten-Schatz', desc: 'Auf hoher See zwischen Segeln und Goldmünzen.' },
    { icon: '🦕', title: 'Dino-Forschung', desc: 'Mit den Urzeit-Riesen auf Entdeckungstour.' },
    { icon: '🐾', title: 'Tier-Klinik', desc: 'Helfer für kleine Fellnasen und Federfreunde.' },
    { icon: '⚔️', title: 'Ritter-Abenteuer', desc: 'Mutig durch Burg und Wald.' }
  ];

  // ---------- Visual-Mappings (Sprint 6) ----------
  // Hair-Swatches — visuelle Farb-Paletten je Option
  const HAIR_COLORS = {
    'Hellblond':   '#F2E2BB',
    'Blond':       '#E8C988',
    'Hellbraun':   '#B5895E',
    'Braun':       '#6E4426',
    'Dunkelbraun': '#3D2417',
    'Schwarz':     '#1F1A14',
    'Rot':         '#B5563E'
  };

  // Eye-Iris-Farben für die SVG-Augen
  const EYE_COLORS = {
    'Hellblau':    '#A8D3E8',
    'Blau':        '#5A82B8',
    'Grün':        '#6FA363',
    'Haselnuss':   '#A07842',
    'Hellbraun':   '#9D7148',
    'Braun':       '#6E4527',
    'Dunkelbraun': '#3D2515'
  };

  // Emoji-Prefix für Geschlecht-Buttons
  const GENDER_EMOJI = {
    'Junge':   '👦',
    'Mädchen': '👧'
  };

  // ==========================================================
  // ============== MODUS-DETECTION (Sprint 6) ================
  // ==========================================================

  function isFunnelView() {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') === 'funnel';
  }

  // PDP-Mode: gar nichts initialisieren — der CTA-Link führt zur Funnel-View
  if (!isFunnelView()) {
    // Klasse abräumen falls Browser-Back von Funnel zur PDP führt
    document.documentElement.classList.remove('is-funnel-mode');
    return;
  }

  // Ab hier: Funnel-Mode aktiv
  document.documentElement.classList.add('is-funnel-mode');

  // ---------- Daten aus Liquid holen ----------
  let DATA = {};
  try {
    const dataEl = document.getElementById('wp-funnel-data');
    DATA = JSON.parse(dataEl.textContent);
  } catch (e) {
    console.error('[funnel] Konnte wp-funnel-data nicht parsen:', e);
    return;
  }

  const root = document.getElementById('wp-funnel');
  if (!root) return;

  // Funnel-Container sichtbar machen (war hidden im Liquid)
  root.hidden = false;

  // ---------- State-Wrapper ----------
  const state = {
    load() {
      try {
        return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
      } catch {
        return {};
      }
    },
    save(data) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    },
    get(key) {
      return this.load()[key];
    },
    set(key, value) {
      const data = this.load();
      data[key] = value;
      this.save(data);
    },
    unset(key) {
      const data = this.load();
      delete data[key];
      this.save(data);
    },
    clear() {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  };

  // ---------- URL-Sync ----------
  function getStepFromURL() {
    const params = new URLSearchParams(window.location.search);
    const step = parseInt(params.get('step'), 10);
    return step >= 1 && step <= TOTAL_STEPS ? step : 1;
  }

  function setStepInURL(step) {
    const params = new URLSearchParams(window.location.search);
    params.set('view', 'funnel');
    params.set('step', String(step));
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ step }, '', newUrl);
  }

  // ---------- Step-Router ----------
  let currentStep = getStepFromURL();

  function showStep(step) {
    root.querySelectorAll('[data-wp-step]').forEach(el => {
      el.hidden = parseInt(el.dataset.wpStep, 10) !== step;
    });

    const counter = root.querySelector('[data-wp-current-step]');
    if (counter) counter.textContent = String(step);

    // Progress-Bar updaten
    const progressFill = root.querySelector('[data-wp-progress-fill]');
    if (progressFill) {
      const pct = Math.round((step / TOTAL_STEPS) * 100);
      progressFill.style.width = pct + '%';
    }

    const backBtn = root.querySelector('[data-wp-action="back"]');
    if (backBtn) backBtn.style.visibility = step === 1 ? 'hidden' : 'visible';

    currentStep = step;
    setStepInURL(step);

    if (step === 2) initTurnstile();
    if (step === 3) {
      startLoadingAnimation();
    } else {
      stopLoadingAnimation();
    }
    if (step === 4) renderBookPreview();
    if (step === 5) ensureBindingDefault();
    if (step === 7) { setSubmitState('idle'); renderStep7Upsell(); }

    updateNextButtonPrice();
    validateCurrentStep();

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function nextStep() {
    if (currentStep < TOTAL_STEPS) {
      showStep(currentStep + 1);
    } else {
      submitOrder();
    }
  }

  function prevStep() {
    if (currentStep > 1) showStep(currentStep - 1);
  }

  // Funnel schließen — zurück zur normalen Produktseite.
  // Wird nach erfolgreichem In-den-Warenkorb genutzt, damit der
  // Side-Cart frei über der Seite liegt statt über dem Funnel.
  function closeFunnel() {
    stopLoadingAnimation();
    document.documentElement.classList.remove('is-funnel-mode');
    if (root) root.hidden = true;
    try {
      history.replaceState({}, '', window.location.pathname);
    } catch (_) {}
    window.scrollTo({ top: 0 });
  }

  // ==========================================================
  // ============== STEP 1 — RENDERINGS =======================
  // ==========================================================

  /**
   * Geschlecht-Buttons: Emoji + Label (Miracles-Style)
   */
  function renderGenderOptions() {
    const group = root.querySelector('[data-wp-field-group="gender"]');
    if (!group || !DATA.options.gender) return;

    group.innerHTML = DATA.options.gender.map(val => {
      const emoji = GENDER_EMOJI[val] || '';
      return `
        <button type="button" class="wp-option wp-option--gender" data-wp-option="gender" data-wp-value="${escapeAttr(val)}">
          ${emoji ? `<span class="wp-option__emoji" aria-hidden="true">${emoji}</span>` : ''}
          <span class="wp-option__label">${escapeHtml(val)}</span>
        </button>
      `;
    }).join('');
  }

  /**
   * Haarfarbe: Color-Swatch-Cards (Farbblock + Label darunter)
   */
  function renderHairOptions() {
    const group = root.querySelector('[data-wp-field-group="hair"]');
    if (!group || !DATA.options.hair) return;

    group.innerHTML = DATA.options.hair.map(val => {
      const color = HAIR_COLORS[val] || '#cccccc';
      return `
        <button type="button" class="wp-option wp-option--swatch" data-wp-option="hair" data-wp-value="${escapeAttr(val)}">
          <span class="wp-swatch wp-swatch--hair" style="background:${color}" aria-hidden="true"></span>
          <span class="wp-option__label">${escapeHtml(val)}</span>
        </button>
      `;
    }).join('');
  }

  /**
   * Augenfarbe: SVG-Eye-Icon mit farbiger Iris + Label
   */
  function renderEyeOptions() {
    const group = root.querySelector('[data-wp-field-group="eyes"]');
    if (!group || !DATA.options.eyes) return;

    group.innerHTML = DATA.options.eyes.map(val => {
      const irisColor = EYE_COLORS[val] || '#888888';
      return `
        <button type="button" class="wp-option wp-option--swatch" data-wp-option="eyes" data-wp-value="${escapeAttr(val)}">
          <span class="wp-swatch wp-swatch--eye" aria-hidden="true">
            <svg viewBox="0 0 40 26" width="40" height="26">
              <ellipse cx="20" cy="13" rx="18" ry="11" fill="#fff" stroke="#2a2a2a" stroke-width="1.4"/>
              <circle cx="20" cy="13" r="7.5" fill="${irisColor}"/>
              <circle cx="20" cy="13" r="3" fill="#0a0a0a"/>
              <circle cx="22" cy="11" r="1.2" fill="#fff" opacity="0.9"/>
            </svg>
          </span>
          <span class="wp-option__label">${escapeHtml(val)}</span>
        </button>
      `;
    }).join('');
  }

  function renderOptions() {
    renderGenderOptions();
    renderHairOptions();
    renderEyeOptions();
  }

  // ---------- State wiederherstellen (nach Refresh/Back) ----------
  function restoreUI() {
    const s = state.load();

    const nameInput = root.querySelector('[data-wp-field="name"]');
    if (nameInput && s.name) nameInput.value = s.name;

    ['gender', 'hair', 'eyes'].forEach(key => {
      if (!s[key]) return;
      const btn = root.querySelector(`[data-wp-option="${key}"][data-wp-value="${cssEscape(s[key])}"]`);
      if (btn) btn.classList.add('is-selected');
    });

    if (s.photo_url) {
      setUploadState('success', { url: s.photo_url });
    }

    const consentCb = root.querySelector('[data-wp-field="photo_consent"]');
    if (consentCb && s.photo_consent === true) {
      consentCb.checked = true;
    }

    if (s.binding) {
      const card = root.querySelector(`[data-wp-binding="${s.binding}"]`);
      if (card) card.classList.add('is-selected');
    }

    if (s.extra_widmung === true) {
      const cb = root.querySelector('[data-wp-extra-toggle="widmung"]');
      if (cb) cb.checked = true;
      const card = root.querySelector('[data-wp-extra-card="widmung"]');
      if (card) card.classList.add('is-selected');
      const detail = root.querySelector('[data-wp-extra-detail="widmung"]');
      if (detail) detail.hidden = false;
      if (s.extra_widmung_text) {
        const ta = root.querySelector('[data-wp-extra-text="widmung"]');
        if (ta) ta.value = s.extra_widmung_text;
        const counter = root.querySelector('[data-wp-extra-counter="widmung"]');
        if (counter) counter.textContent = String(s.extra_widmung_text.length);
      }
    }
    if (s.extra_premium_papier === true) {
      const cb = root.querySelector('[data-wp-extra-toggle="premium_papier"]');
      if (cb) cb.checked = true;
      const card = root.querySelector('[data-wp-extra-card="premium_papier"]');
      if (card) card.classList.add('is-selected');
    }
  }

  // ---------- Step-Validierung ----------
  function validateCurrentStep() {
    const s = state.load();
    const nextBtn = root.querySelector('[data-wp-action="next"]');
    if (!nextBtn) return;

    let valid = true;

    if (currentStep === 1) {
      valid = Boolean(s.name && s.name.trim().length >= 2 && s.gender && s.hair && s.eyes);
    } else if (currentStep === 2) {
      valid = Boolean(s.photo_url && s.photo_consent === true);
    } else if (currentStep === 3) {
      valid = Boolean(s.preview_generated === true);
    } else if (currentStep === 4) {
      valid = true;
    } else if (currentStep === 5) {
      valid = Boolean(s.binding && s.binding_variant_id);
    } else if (currentStep === 6) {
      if (s.extra_widmung === true) {
        valid = Boolean(s.extra_widmung_text && s.extra_widmung_text.trim().length > 0);
      } else {
        valid = true;
      }
    }

    nextBtn.disabled = !valid;
  }

  // ==========================================================
  // ==================== STEP 2 — UPLOAD =====================
  // ==========================================================

  const uploadEls = {};
  function cacheUploadEls() {
    uploadEls.root      = root.querySelector('[data-wp-upload-root]');
    uploadEls.dropzone  = root.querySelector('[data-wp-dropzone]');
    uploadEls.input     = root.querySelector('[data-wp-upload-input]');
    uploadEls.progress  = root.querySelector('[data-wp-upload-progress]');
    uploadEls.fill      = root.querySelector('[data-wp-upload-fill]');
    uploadEls.label     = root.querySelector('[data-wp-upload-label]');
    uploadEls.preview   = root.querySelector('[data-wp-upload-preview]');
    uploadEls.thumb     = root.querySelector('[data-wp-upload-thumb]');
    uploadEls.error     = root.querySelector('[data-wp-upload-error]');
    uploadEls.errorMsg  = root.querySelector('[data-wp-upload-error-msg]');
    uploadEls.consent   = root.querySelector('[data-wp-consent-wrap]');
    uploadEls.consentStatus = root.querySelector('[data-wp-consent-status]');
  }

  function setUploadState(stateName, payload = {}) {
    if (!uploadEls.root) return;
    uploadEls.root.dataset.wpUploadState = stateName;

    if (uploadEls.dropzone) uploadEls.dropzone.hidden = (stateName !== 'empty');
    if (uploadEls.progress) uploadEls.progress.hidden = (stateName !== 'uploading');
    if (uploadEls.preview)  uploadEls.preview.hidden  = (stateName !== 'success');
    if (uploadEls.error)    uploadEls.error.hidden    = (stateName !== 'error');

    if (uploadEls.consent) {
      uploadEls.consent.hidden = (stateName !== 'success');
    }
    // Status-Zeile des Bot-Checks nur bei sichtbarer Consent-Box behalten
    if (uploadEls.consentStatus && stateName !== 'success') {
      uploadEls.consentStatus.hidden = true;
    }

    if (stateName === 'uploading') setProgress(payload.progress || 0);
    if (stateName === 'success' && payload.url && uploadEls.thumb) {
      uploadEls.thumb.src = payload.url;
    }
    if (stateName === 'error' && payload.message && uploadEls.errorMsg) {
      uploadEls.errorMsg.textContent = payload.message;
    }
  }

  function setProgress(percent) {
    const p = Math.max(0, Math.min(100, Math.round(percent)));
    if (uploadEls.fill)  uploadEls.fill.style.width = p + '%';
    if (uploadEls.label) uploadEls.label.textContent = `Wird hochgeladen … ${p} %`;
  }

  function validateFile(file) {
    if (!file) return 'Keine Datei ausgewählt.';
    if (!CLOUDINARY.allowedTypes.includes(file.type)) {
      return 'Bitte ein Bild als JPG, PNG oder WebP auswählen.';
    }
    if (file.size > CLOUDINARY.maxBytes) {
      const mb = (file.size / 1024 / 1024).toFixed(1);
      return `Das Bild ist ${mb} MB groß. Maximal 10 MB erlaubt.`;
    }
    return null;
  }

  function uploadToCloudinary(file) {
    const validationError = validateFile(file);
    if (validationError) {
      setUploadState('error', { message: validationError });
      return;
    }

    state.unset('photo_url');
    state.unset('photo_public_id');
    state.unset('preview_generated');
    state.unset('character_image_url');
    state.unset('cover_image_url');
    validateCurrentStep();

    setUploadState('uploading', { progress: 0 });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY.uploadPreset);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', CLOUDINARY.endpoint, true);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) setProgress((e.loaded / e.total) * 100);
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const res = JSON.parse(xhr.responseText);
          if (!res.secure_url) throw new Error('Antwort ohne secure_url');
          state.set('photo_url', res.secure_url);
          state.set('photo_public_id', res.public_id);
          setUploadState('success', { url: res.secure_url });
          validateCurrentStep();
          console.log('[funnel] Foto hochgeladen:', res.secure_url);
        } catch (err) {
          console.error('[funnel] Cloudinary-Response fehlerhaft:', err, xhr.responseText);
          setUploadState('error', { message: 'Upload-Antwort konnte nicht gelesen werden. Bitte erneut versuchen.' });
        }
      } else {
        let msg = `Upload fehlgeschlagen (Status ${xhr.status}).`;
        try {
          const err = JSON.parse(xhr.responseText);
          if (err && err.error && err.error.message) msg = `Upload fehlgeschlagen: ${err.error.message}`;
        } catch {}
        console.error('[funnel] Cloudinary-Fehler:', xhr.status, xhr.responseText);
        setUploadState('error', { message: msg });
      }
    });

    xhr.addEventListener('error', () => {
      setUploadState('error', { message: 'Keine Verbindung zum Upload-Server. Internetverbindung prüfen und erneut versuchen.' });
    });

    xhr.addEventListener('abort', () => {
      setUploadState('error', { message: 'Upload wurde abgebrochen.' });
    });

    xhr.send(formData);
  }

  function openFilePicker() {
    if (uploadEls.input) {
      uploadEls.input.value = '';
      uploadEls.input.click();
    }
  }

  function bindUploadEvents() {
    if (!uploadEls.root) return;

    if (uploadEls.input) {
      uploadEls.input.addEventListener('change', (e) => {
        const file = e.target.files && e.target.files[0];
        if (file) uploadToCloudinary(file);
      });
    }

    if (uploadEls.dropzone) {
      ['dragenter', 'dragover'].forEach(evt => {
        uploadEls.dropzone.addEventListener(evt, (e) => {
          e.preventDefault();
          e.stopPropagation();
          uploadEls.dropzone.classList.add('is-dragover');
        });
      });
      ['dragleave', 'dragend', 'drop'].forEach(evt => {
        uploadEls.dropzone.addEventListener(evt, (e) => {
          e.preventDefault();
          e.stopPropagation();
          uploadEls.dropzone.classList.remove('is-dragover');
        });
      });
      uploadEls.dropzone.addEventListener('drop', (e) => {
        const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) uploadToCloudinary(file);
      });
    }
  }

  // ==========================================================
  // ====== STEP 2 — ANTI-ROBOTER-CHECK (Cloudflare Turnstile)
  // ==========================================================
  //
  // Unsichtbarer Bot-Check, gekoppelt an die Rechte-Bestätigung:
  // Sobald der Kunde die Checkbox "Ich habe die Rechte am Foto"
  // anhakt, läuft im Hintergrund ein Cloudflare-Turnstile-Check.
  // Erkennt Cloudflare einen Bot, wird die Bestätigung zurückgesetzt
  // und der Funnel blockiert — ein echter Mensch merkt nichts davon
  // und kommt ganz normal durch.
  //
  // Ist kein Site-Key hinterlegt (Section-Setting turnstile_site_key),
  // entfällt der Check komplett: die Bestätigung wirkt sofort.
  //
  // WICHTIG: Das Turnstile-Widget muss im Cloudflare-Dashboard im
  // Modus "Invisible" angelegt sein. Der bestandene Token landet im
  // State (turnstile_token) und wandert in Schritt 3 mit ins n8n-Paket.

  const TURNSTILE_API = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

  let turnstileWidgetId   = null;
  let turnstileScriptBusy = false;
  let turnstileHost       = null;
  let botCheckPending     = false;

  function captchaRequired() {
    return Boolean(DATA.turnstile_site_key);
  }

  // Liefert den frischesten verfügbaren Token (Widget bevorzugt).
  function freshTurnstileToken() {
    try {
      if (turnstileWidgetId !== null && window.turnstile &&
          typeof window.turnstile.getResponse === 'function') {
        const live = window.turnstile.getResponse(turnstileWidgetId);
        if (live) return live;
      }
    } catch (_) {}
    return state.get('turnstile_token') || '';
  }

  // Verstecktes Container-Div fürs (unsichtbare) Turnstile-Widget.
  function turnstileContainer() {
    if (turnstileHost) return turnstileHost;
    turnstileHost = document.createElement('div');
    turnstileHost.setAttribute('data-wp-turnstile-host', '');
    turnstileHost.style.display = 'none';
    root.appendChild(turnstileHost);
    return turnstileHost;
  }

  // Script laden + Widget rendern. execution:'execute' → der Check
  // läuft erst auf turnstile.execute(), nicht schon beim Rendern.
  function initTurnstile() {
    if (!captchaRequired() || turnstileWidgetId !== null) return;

    if (window.turnstile && typeof window.turnstile.render === 'function') {
      renderTurnstile();
      return;
    }
    if (turnstileScriptBusy) return;
    turnstileScriptBusy = true;

    const script = document.createElement('script');
    script.src = TURNSTILE_API;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', renderTurnstile);
    script.addEventListener('error', () => {
      console.error('[funnel] Turnstile-Script konnte nicht geladen werden.');
    });
    document.head.appendChild(script);
  }

  function renderTurnstile() {
    if (turnstileWidgetId !== null || !window.turnstile) return;
    try {
      turnstileWidgetId = window.turnstile.render(turnstileContainer(), {
        sitekey: DATA.turnstile_site_key,
        execution: 'execute',
        appearance: 'interaction-only',
        action: 'photo_consent',
        callback: onTurnstilePass,
        'error-callback': onTurnstileFail,
        'expired-callback': function () { state.unset('turnstile_token'); }
      });
    } catch (e) {
      console.error('[funnel] Turnstile-Render fehlgeschlagen:', e);
    }
    // Hat der Kunde die Checkbox angehakt, bevor das Widget bereit
    // war, holen wir den Check jetzt nach.
    if (botCheckPending) {
      botCheckPending = false;
      executeBotCheck();
    }
  }

  // Startet den unsichtbaren Bot-Check.
  function executeBotCheck() {
    if (turnstileWidgetId === null || !window.turnstile) {
      botCheckPending = true;
      initTurnstile();
      return;
    }
    try {
      window.turnstile.reset(turnstileWidgetId);
      window.turnstile.execute(turnstileWidgetId, { action: 'photo_consent' });
    } catch (e) {
      console.error('[funnel] Turnstile-Execute fehlgeschlagen:', e);
      // Im Zweifel echte Kunden nicht aussperren — als bestanden werten.
      onTurnstilePass('');
    }
  }

  function onTurnstilePass(token) {
    state.set('turnstile_token', token || '');
    const cb = root.querySelector('[data-wp-field="photo_consent"]');
    if (cb && cb.checked) state.set('photo_consent', true);
    setConsentStatus('ok');
    validateCurrentStep();
  }

  function onTurnstileFail() {
    state.unset('turnstile_token');
    state.set('photo_consent', false);
    const cb = root.querySelector('[data-wp-field="photo_consent"]');
    if (cb) cb.checked = false;
    setConsentStatus('error');
    validateCurrentStep();
  }

  // Status-Zeile unter der Consent-Checkbox: 'checking' | 'error' | 'ok'
  function setConsentStatus(mode) {
    const el = root.querySelector('[data-wp-consent-status]');
    if (!el) return;
    if (mode === 'checking') {
      el.hidden = false;
      el.dataset.state = 'checking';
      el.textContent = 'Kurzer Sicherheits-Check läuft …';
    } else if (mode === 'error') {
      el.hidden = false;
      el.dataset.state = 'error';
      el.textContent = 'Bestätigung konnte nicht verifiziert werden. Bitte erneut anhaken.';
    } else {
      el.hidden = true;
      el.dataset.state = 'ok';
      el.textContent = '';
    }
  }

  // Reaktion auf die Rechte-Checkbox (statt der generischen Feld-Logik).
  function handleConsentChange(cb) {
    if (!cb.checked) {
      state.set('photo_consent', false);
      state.unset('turnstile_token');
      setConsentStatus('ok'); // versteckt die Statuszeile
      validateCurrentStep();
      return;
    }
    // Angehakt — ohne Site-Key wirkt die Bestätigung sofort.
    if (!captchaRequired()) {
      state.set('photo_consent', true);
      validateCurrentStep();
      return;
    }
    // Mit Captcha: erst nach bestandenem Bot-Check gültig.
    state.set('photo_consent', false);
    setConsentStatus('checking');
    validateCurrentStep();
    executeBotCheck();
  }

  // ==========================================================
  // ============== STEP 3 — LIVE-VORSCHAU ====================
  // ==========================================================
  //
  // Schickt die Kundendaten an den n8n-Webhook (Section-Setting
  // preview_webhook_url). n8n erzeugt das Charakterbild und gibt es
  // als character_image_url zurück. Während n8n arbeitet, läuft die
  // Fortschrittsanzeige; danach geht es weiter zu Step 4.
  //
  // Ist keine Webhook-URL hinterlegt, läuft der alte Timer-Stub als
  // Fallback, damit der Funnel trotzdem durchläuft.
  // Daten-Contract: siehe LIVE_VORSCHAU_WEBHOOK.md

  const PREVIEW_TIMEOUT_MS = 90000; // n8n-Antwort spätestens nach 90 s

  let loadingTimer    = null;
  let previewAbort    = null;
  let previewInFlight = false;
  const loadingEls = {};

  function cacheLoadingEls() {
    loadingEls.fill     = root.querySelector('[data-wp-loading-fill]');
    loadingEls.percent  = root.querySelector('[data-wp-loading-percent]');
    loadingEls.status   = root.querySelector('[data-wp-loading-status]');
    loadingEls.substeps = Array.from(root.querySelectorAll('[data-wp-substep]'));
    loadingEls.main     = root.querySelector('[data-wp-loading-main]');
    loadingEls.error    = root.querySelector('[data-wp-loading-error]');
    loadingEls.errorMsg = root.querySelector('[data-wp-loading-error-msg]');
  }

  function setLoadingPercent(p) {
    const v = Math.max(0, Math.min(100, p));
    if (loadingEls.fill)    loadingEls.fill.style.width = v + '%';
    if (loadingEls.percent) loadingEls.percent.textContent = Math.round(v) + ' %';

    if (v >= 100) {
      loadingEls.substeps.forEach(el => {
        el.classList.add('is-done');
        el.classList.remove('is-active');
      });
      return;
    }

    const activeIdx = LOADING_SUBSTEPS.reduce((acc, s, i) => v >= s.from ? i : acc, 0);
    loadingEls.substeps.forEach((el, i) => {
      el.classList.toggle('is-done',   i <  activeIdx);
      el.classList.toggle('is-active', i === activeIdx);
    });
  }

  function setLoadingStatus(text) {
    if (loadingEls.status) loadingEls.status.textContent = text;
  }

  function stopLoadingAnimation() {
    if (loadingTimer) {
      clearInterval(loadingTimer);
      loadingTimer = null;
    }
  }

  function showLoadingError(message) {
    stopLoadingAnimation();
    if (loadingEls.main)  loadingEls.main.hidden  = true;
    if (loadingEls.error) loadingEls.error.hidden = false;
    if (loadingEls.errorMsg && message) loadingEls.errorMsg.textContent = message;
  }

  function clearLoadingError() {
    if (loadingEls.main)  loadingEls.main.hidden  = false;
    if (loadingEls.error) loadingEls.error.hidden = true;
  }

  // Fortschrittsbalken läuft sanft hoch — bis zur Obergrenze `cap`.
  // Bei cap < 100 hält er dort und wartet auf die echte n8n-Antwort.
  function startProgressCreep(cap) {
    stopLoadingAnimation();
    const limit = cap || 100;
    const start = Date.now();
    setLoadingPercent(0);
    setLoadingStatus(LOADING_SUBSTEPS[0].label);

    loadingTimer = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(limit, (elapsed / LOADING_DURATION_MS) * 100);
      setLoadingPercent(pct);

      const sub = LOADING_SUBSTEPS.slice().reverse().find(st => pct >= st.from);
      if (sub) setLoadingStatus(sub.label);

      if (limit >= 100 && pct >= 100) finishLoading();
    }, 100);
  }

  // Vorschau fertig → auf 100 %, merken, weiter zu Step 4.
  function finishLoading() {
    stopLoadingAnimation();
    setLoadingPercent(100);
    setLoadingStatus('Fertig');
    state.set('preview_generated', true);
    validateCurrentStep();
    setTimeout(() => {
      if (getStepFromURL() === 3) showStep(4);
    }, 600);
  }

  // Datenpaket für n8n — siehe LIVE_VORSCHAU_WEBHOOK.md (Daten-Contract).
  function buildPreviewPayload(s) {
    return {
      request_id:      'wp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
      book:            (DATA.product && DATA.product.handle) || '',
      name:            s.name || '',
      gender:          s.gender || '',
      hair:            s.hair || '',
      hair_hex:        HAIR_COLORS[s.hair] || '',
      eyes:            s.eyes || '',
      eyes_hex:        EYE_COLORS[s.eyes] || '',
      photo_url:       s.photo_url || '',
      photo_public_id: s.photo_public_id || '',
      turnstile_token: freshTurnstileToken()
    };
  }

  async function requestPreview() {
    clearLoadingError();
    startProgressCreep(92); // visuell — hält bei 92 %, bis n8n antwortet

    if (previewInFlight) return; // kein zweiter Aufruf parallel
    previewInFlight = true;

    const url = DATA.preview_webhook_url;
    const payload = buildPreviewPayload(state.load());
    console.log('[funnel] Vorschau-Anfrage an n8n:', payload);

    previewAbort = new AbortController();
    const timeout = setTimeout(() => previewAbort.abort(), PREVIEW_TIMEOUT_MS);

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
        signal: previewAbort.signal
      });
      clearTimeout(timeout);

      if (!res.ok) throw new Error('Server-Status ' + res.status);

      const data = await res.json();
      if (!data || data.status !== 'ok' || !(data.character_image_url || data.cover_image_url)) {
        throw new Error((data && data.message) || 'Unerwartete Antwort von n8n');
      }

      // n8n (Marco) liefert genau EIN personalisiertes Bild für die Vorschau:
      // das Charakter-/Cover-Bild, das in Schritt 4 ganz oben als Hero steht.
      // Alle weiteren Seiten der Vorschau sind statisch (funnel_book_v2) und
      // werden im Browser zusammengesetzt — nur der Name wird per JS eingefügt.
      if (data.character_image_url) state.set('character_image_url', data.character_image_url);
      if (data.cover_image_url)     state.set('cover_image_url', data.cover_image_url);
      console.log('[funnel] Vorschau-Bild erhalten:', {
        cover: data.cover_image_url || null,
        character: data.character_image_url || null
      });
      finishLoading();
    } catch (err) {
      clearTimeout(timeout);
      console.error('[funnel] Vorschau-Fehler:', err);
      const msg = (err && err.name === 'AbortError')
        ? 'Das hat diesmal zu lange gedauert. Bitte versuch es noch einmal.'
        : 'Die Vorschau konnte gerade nicht erstellt werden. Bitte versuch es noch einmal.';
      showLoadingError(msg);
    } finally {
      previewInFlight = false;
    }
  }

  // Fallback ohne Webhook-URL — reine Lade-Animation wie bisher.
  function runFallbackTimer() {
    startProgressCreep(100);
  }

  function startLoadingAnimation() {
    stopLoadingAnimation();
    clearLoadingError();

    const s = state.load();

    // Vorschau schon erzeugt (Refresh / Zurück) → direkt auf 100 %.
    if (s.preview_generated === true &&
        (s.cover_image_url || s.character_image_url || !DATA.preview_webhook_url)) {
      setLoadingPercent(100);
      setLoadingStatus('Fertig');
      validateCurrentStep();
      return;
    }

    if (DATA.preview_webhook_url) {
      if (previewInFlight) {
        startProgressCreep(92); // läuft schon — nur die Anzeige zeigen
      } else {
        requestPreview();
      }
    } else {
      runFallbackTimer();
    }
  }

  function retryPreview() {
    state.unset('preview_generated');
    state.unset('character_image_url');
    state.unset('cover_image_url');
    startLoadingAnimation();
  }

  // Vorschau verwerfen, sobald sich ein Merkmal ändert (Name/Geschlecht/Haar/Augen).
  function invalidatePreview() {
    state.unset('preview_generated');
    state.unset('character_image_url');
    state.unset('cover_image_url');
  }

  // ==========================================================
  // =========== STEP 4 — BUCH-MOCKUP-VORSCHAU ================
  // ==========================================================
  //
  // Liest DATA.book (JSON-Metafield funnel_book_v2) und baut das Buch-Mockup
  // komplett im Browser zusammen (Hero → Cover → Widmung → Story-Pages → Outro):
  //   • Hero oben  = das EINE personalisierte Bild von n8n/Marco (Charakter/Cover).
  //   • Alles übrige = statische, pro Buch fest hinterlegte Bilder + Texte.
  //   • Einzige Personalisierung im Browser: der {NAME} wird in Cover-Titel,
  //     Widmung und Story-Texte eingesetzt.
  //
  // Fällt auf das alte Schema (DATA.preview.boy/girl + DATA.story_preview)
  // zurück, falls book_v2 nicht gesetzt ist.

  function renderBookPreview() {
    const s = state.load();
    const container = root.querySelector('[data-wp-book]');
    const fallbackEl = root.querySelector('[data-wp-book-fallback]');
    if (!container) return;

    const book = DATA.book;
    const hasLegacy = Boolean(DATA.preview && (DATA.preview.boy || DATA.preview.girl));

    // Weder neues Schema noch Legacy → Fallback-Hinweis zeigen
    if (!book && !hasLegacy) {
      if (fallbackEl) {
        container.innerHTML = '';
        container.appendChild(fallbackEl);
        fallbackEl.hidden = false;
      }
      return;
    }

    if (fallbackEl) fallbackEl.hidden = true;

    if (book) {
      container.innerHTML = renderBookMockup(book, s);
    } else {
      container.innerHTML = renderLegacyPreview(s);
    }
  }

  function renderBookMockup(book, s) {
    const name = (s.name || 'dein Kind').trim();
    const fallbackChar = s.gender === 'Mädchen'
      ? (book.character_default_girl || book.character_default_boy)
      : (book.character_default_boy || book.character_default_girl);
    // Hero-Bild ganz oben: bevorzugt das von n8n erzeugte Buchcover,
    // sonst das freigestellte Charakterbild, sonst das Standard-Bild.
    const heroImage = s.cover_image_url || s.character_image_url || fallbackChar;
    const heroIsCover = Boolean(s.cover_image_url);

    const parts = [];

    // Hero ganz oben — das personalisierte Cover (bzw. die Charakter-Pose).
    if (heroImage) {
      parts.push(`
        <div class="wp-book__hero${heroIsCover ? ' wp-book__hero--cover' : ''}">
          <img src="${escapeAttr(heroImage)}" alt="" onerror="this.parentElement.style.display='none'">
        </div>
      `);
    }

    // Cover-Card
    if (book.cover) {
      const coverTitleRaw = book.cover.title_template || 'Dein Abenteuer';
      const coverTitle = replaceName(coverTitleRaw, name);
      const coverSubtitle = book.cover.subtitle || '';
      const coverImg = book.cover.image_url
        ? `<img class="wp-book__cover-img" src="${escapeAttr(book.cover.image_url)}" alt="" onerror="this.style.display='none'">`
        : '';
      parts.push(`
        <div class="wp-book__page wp-book__page--cover">
          ${coverImg}
          <div class="wp-book__cover-text">
            <h2 class="wp-book__cover-title">${escapeHtml(coverTitle)}</h2>
            ${coverSubtitle ? `<p class="wp-book__cover-subtitle">${escapeHtml(coverSubtitle)}</p>` : ''}
          </div>
        </div>
      `);
    }

    // Widmungsseite — immer fest gerendert mit Name
    parts.push(`
      <div class="wp-book__page wp-book__page--dedication">
        <span class="wp-book__dedication-label">Dieses Buch gehört</span>
        <span class="wp-book__dedication-name">${escapeHtml(name)}</span>
      </div>
    `);

    // Story-Pages — jede Seite ist ein statisches Hintergrundbild (1× pro Buch
    // erstellt, verblasster Charakter + Seitenzahl bereits eingebacken) mit dem
    // Story-Text als Overlay. Nur der {NAME} wird hier per JS eingesetzt.
    // Seiten-Logik nach vorhandenen Feldern:
    //   image_url + text → Spread (Bild mit Text-Overlay)  ← Standard ab jetzt
    //   nur image_url    → reines Bild
    //   nur text         → Theme-Color-Textseite (Fallback ohne Bild)
    if (Array.isArray(book.pages)) {
      book.pages.forEach(page => {
        if (!page) return;
        const hasImg  = Boolean(page.image_url);
        const hasText = Boolean(page.text);
        if (!hasImg && !hasText) return;

        const pageNum = page.page_number
          ? `<span class="wp-book__page-number">${escapeHtml(String(page.page_number))}</span>`
          : '';

        // Story-Text aufbereiten: Name einsetzen → Absätze
        let textHtml = '';
        if (hasText) {
          textHtml = replaceName(page.text, name)
            .split(/\n\s*\n/)
            .map(p => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
            .join('');
        }

        if (hasImg && hasText) {
          // Spread: statisches Bild + Text-Overlay mit Name.
          // text_position (top|center|bottom) steuert, wo der Text sitzt —
          // passend zur freien Fläche der jeweiligen Illustration.
          const pos = ['top', 'center', 'bottom'].indexOf(page.text_position) >= 0
            ? page.text_position
            : 'bottom';
          parts.push(`
            <div class="wp-book__page wp-book__page--spread wp-book__page--pos-${pos}">
              <img class="wp-book__spread-img" src="${escapeAttr(page.image_url)}" alt="" onerror="this.closest('.wp-book__page--spread').classList.add('wp-book__page--noimg'); this.style.display='none'">
              <div class="wp-book__spread-text">
                <div class="wp-book__page-text">${textHtml}</div>
              </div>
              ${pageNum}
            </div>
          `);
        } else if (hasImg) {
          parts.push(`
            <div class="wp-book__page wp-book__page--image">
              <img src="${escapeAttr(page.image_url)}" alt="" onerror="this.closest('.wp-book__page--image').style.display='none'">
              ${pageNum}
            </div>
          `);
        } else {
          parts.push(`
            <div class="wp-book__page wp-book__page--text">
              <div class="wp-book__page-text">${textHtml}</div>
              ${pageNum}
            </div>
          `);
        }
      });
    }

    // Outro — der psychologisch entscheidende Hinweis
    const remaining = (typeof book.outro_remaining_pages === 'number')
      ? book.outro_remaining_pages
      : 20;
    parts.push(`
      <div class="wp-book__outro">
        <p>Die restlichen <strong>${escapeHtml(String(remaining))} Seiten</strong> werden nach dem Kauf fertiggestellt. Die verpixelten Stellen verschwinden — dort setzt unser Designerteam deinen Charakter individuell ein. <span aria-hidden="true">✨</span></p>
      </div>
    `);

    return parts.join('');
  }

  function renderLegacyPreview(s) {
    // Fallback: altes Schema (preview_boy/girl + story_preview)
    const name = (s.name || 'dein Kind').trim();
    const url = s.cover_image_url || s.character_image_url
              || (s.gender === 'Mädchen' ? DATA.preview.girl : DATA.preview.boy);
    const rawStory = (DATA.story_preview || '').trim();

    const parts = [];

    if (url) {
      parts.push(`
        <div class="wp-book__hero${s.cover_image_url ? ' wp-book__hero--cover' : ''}">
          <img src="${escapeAttr(url)}" alt="">
        </div>
      `);
    }

    if (rawStory) {
      const titleText = s.name ? `${germanPossessive(s.name)} Abenteuer` : 'Deine Geschichte';
      const storyReplaced = replaceName(rawStory, name);
      const paragraphs = storyReplaced
        .split(/\n\s*\n/)
        .map(p => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
        .join('');
      parts.push(`
        <div class="wp-book__page wp-book__page--text">
          <h3 class="wp-book__legacy-title">${escapeHtml(titleText)}</h3>
          <div class="wp-book__page-text">${paragraphs}</div>
        </div>
      `);
    }

    parts.push(`
      <div class="wp-book__outro">
        <p>Das ist eine Vorschau. Die fertige Version deines Buches gestalten wir in unserem Studio. <span aria-hidden="true">✨</span></p>
      </div>
    `);

    return parts.join('');
  }

  function replaceName(text, name) {
    if (!text) return '';
    // Unterstützt {NAME}, {name}, [Name], [NAME], [name] — alle Varianten
    return String(text)
      .replace(/\{NAME\}/gi, name)
      .replace(/\[NAME\]/gi, name);
  }

  function germanPossessive(name) {
    if (!name) return '';
    return /[sßxz]$/i.test(name) ? `${name}'` : `${name}s`;
  }

  // ==========================================================
  // ============== STEP 5 — BINDING ==========================
  // ==========================================================

  function formatEUR(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    return (cents / 100).toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function getBindingVariantMap() {
    const map = {};
    const variants = (DATA.product && DATA.product.variants) || [];
    BINDINGS.forEach(b => {
      // 1. Primär: SKU-Suffix-Match (sauber, wenn die SKUs gepflegt sind)
      let v = variants.find(x => x.sku && x.sku.toUpperCase().endsWith(b.skuSuffix));
      // 2. Fallback: Varianten-Titel matcht das Binding-Label — greift bei
      //    Büchern, deren Varianten (noch) keine SKU haben (z.B. Superheld).
      if (!v) {
        v = variants.find(x => x.title &&
          x.title.trim().toLowerCase() === b.label.toLowerCase());
      }
      if (v) map[b.code] = v;
    });
    return map;
  }

  function renderBindingOptions() {
    const container = root.querySelector('[data-wp-bindings]');
    if (!container) return;

    const map = getBindingVariantMap();

    const html = BINDINGS.map(b => {
      const v = map[b.code];
      if (!v) return '';

      const badge = b.recommended
        ? '<span class="wp-binding-card__badge">Empfehlung</span>'
        : '';

      return `
        <button
          type="button"
          class="wp-binding-card"
          data-wp-binding="${b.code}"
          data-wp-variant-id="${v.id}"
          data-wp-price="${v.price}"
        >
          ${badge}
          <span class="wp-binding-card__check" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 12 10 18 20 6"></polyline></svg>
          </span>
          <span class="wp-binding-card__head">
            <span class="wp-binding-card__title">${escapeHtml(b.label)}</span>
            <span class="wp-binding-card__price">${escapeHtml(formatEUR(v.price))}</span>
          </span>
          <span class="wp-binding-card__desc">${escapeHtml(b.desc)}</span>
        </button>
      `;
    }).join('');

    container.innerHTML = html || '<p class="wp-step__subtitle">Keine Bindungs-Optionen verfügbar.</p>';
  }

  function selectBinding(code, variantId, price) {
    if (!code || !variantId || isNaN(price)) return;
    state.set('binding', code);
    state.set('binding_variant_id', variantId);
    state.set('binding_price', price);

    root.querySelectorAll('[data-wp-binding]').forEach(el => {
      el.classList.toggle('is-selected', el.dataset.wpBinding === code);
    });

    updateNextButtonPrice();
    validateCurrentStep();
  }

  function ensureBindingDefault() {
    const s = state.load();
    if (s.binding && s.binding_variant_id) return;

    const map = getBindingVariantMap();
    const defaultEntry = BINDINGS.find(b => b.isDefault && map[b.code])
                      || BINDINGS.find(b => map[b.code]);
    if (!defaultEntry) return;

    const v = map[defaultEntry.code];
    selectBinding(defaultEntry.code, v.id, v.price);
  }

  // ==========================================================
  // ============== STEP 6 — EXTRAS ===========================
  // ==========================================================

  function renderExtras() {
    const container = root.querySelector('[data-wp-extras]');
    if (!container) return;

    const widmung = DATA.extras && DATA.extras.widmung;
    const premium = DATA.extras && DATA.extras.premium_papier;

    const parts = [];

    if (widmung && widmung.variant_id) {
      parts.push(`
        <div class="wp-extra-card" data-wp-extra-card="widmung">
          <label class="wp-extra-card__row">
            <input type="checkbox" class="wp-toggle__input" data-wp-extra-toggle="widmung">
            <span class="wp-toggle"><span class="wp-toggle__knob"></span></span>
            <span class="wp-extra-card__main">
              <span class="wp-extra-card__title">Persönliche Widmung</span>
              <span class="wp-extra-card__desc">Eine kleine Botschaft auf der ersten Seite — z.B. zum Geburtstag oder einfach zum Kuscheln.</span>
            </span>
            <span class="wp-extra-card__price">+ ${escapeHtml(formatEUR(widmung.price))}</span>
          </label>
          <div class="wp-extra-card__detail" data-wp-extra-detail="widmung" hidden>
            <textarea
              class="wp-extra-card__textarea"
              data-wp-extra-text="widmung"
              maxlength="200"
              rows="3"
              placeholder="z.B. Für Max, zum 5. Geburtstag — von Oma"></textarea>
            <div class="wp-extra-card__counter"><span data-wp-extra-counter="widmung">0</span> / 200</div>
          </div>
        </div>
      `);
    }

    if (premium && premium.variant_id) {
      parts.push(`
        <div class="wp-extra-card" data-wp-extra-card="premium_papier">
          <label class="wp-extra-card__row">
            <input type="checkbox" class="wp-toggle__input" data-wp-extra-toggle="premium_papier">
            <span class="wp-toggle"><span class="wp-toggle__knob"></span></span>
            <span class="wp-extra-card__main">
              <span class="wp-extra-card__title">Premium-Papier</span>
              <span class="wp-extra-card__desc">Dickeres, mattes Papier — fühlt sich hochwertiger an und hält mehr aus.</span>
            </span>
            <span class="wp-extra-card__price">+ ${escapeHtml(formatEUR(premium.price))}</span>
          </label>
        </div>
      `);
    }

    container.innerHTML = parts.join('') || '<p class="wp-step__subtitle">Aktuell keine Extras verfügbar.</p>';
  }

  function toggleExtra(key, isOn) {
    if (key === 'widmung') {
      state.set('extra_widmung', isOn);
      const detail = root.querySelector('[data-wp-extra-detail="widmung"]');
      if (detail) detail.hidden = !isOn;
      if (!isOn) {
        state.unset('extra_widmung_text');
        const ta = root.querySelector('[data-wp-extra-text="widmung"]');
        if (ta) ta.value = '';
        const counter = root.querySelector('[data-wp-extra-counter="widmung"]');
        if (counter) counter.textContent = '0';
      }
    } else if (key === 'premium_papier') {
      state.set('extra_premium_papier', isOn);
    } else {
      return;
    }

    const card = root.querySelector(`[data-wp-extra-card="${key}"]`);
    if (card) card.classList.toggle('is-selected', isOn);

    updateNextButtonPrice();
    validateCurrentStep();
  }

  function calcTotal() {
    const s = state.load();
    let total = (typeof s.binding_price === 'number') ? s.binding_price : 0;
    if (s.extra_widmung && DATA.extras && DATA.extras.widmung) total += DATA.extras.widmung.price;
    if (s.extra_premium_papier && DATA.extras && DATA.extras.premium_papier) total += DATA.extras.premium_papier.price;
    return total;
  }

  function updateNextButtonPrice() {
    const labelEl = root.querySelector('[data-wp-next-label]');
    if (!labelEl) return;

    const baseText = currentStep === TOTAL_STEPS ? 'In den Warenkorb' : 'Weiter';

    if (currentStep < 5) {
      labelEl.textContent = baseText;
      return;
    }

    const total = calcGrandTotal();
    if (total > 0) {
      labelEl.textContent = `${baseText} — ${formatEUR(total)}`;
    } else {
      labelEl.textContent = baseText;
    }
  }

  // ==========================================================
  // ============== STEP 7 — UPSELL + SUBMIT ==================
  // ==========================================================

  // ---------- Upsell: echte Bücher, gleiche Personalisierung, Bündel-Rabatt ----------
  function fmtMoney(cents) {
    return (Math.round(cents) / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function pickUpsellVariant(book, bindingCode) {
    if (!book || !book.variants || !book.variants.length) return null;
    const suffix = '-' + (bindingCode || 'HC');
    let v = book.variants.find(function (x) { return x.sku && String(x.sku).toUpperCase().endsWith(suffix) && x.available; });
    if (!v) v = book.variants.find(function (x) { return x.available; }) || book.variants[0];
    return v;
  }

  function getUpsellSelected() {
    const arr = state.get('upsell_selected');
    return Array.isArray(arr) ? arr : [];
  }

  // Bündel-Rechnung — spiegelt exakt die Shopify Discount Function im Checkout:
  // teuerstes Buch (= Hauptbuch) bleibt voll, 1. Zusatzbuch −25 %, jedes weitere −40 %.
  // Rechnet zusätzlich das aktuelle Buch + Extras mit, damit der Ledger den echten
  // "aktuell vs. neu"-Vergleich zeigt.
  function calcBundle() {
    const s = state.load();
    const mainPrice  = (typeof s.binding_price === 'number') ? s.binding_price : 0;
    const extrasTotal =
      ((s.extra_widmung && DATA.extras && DATA.extras.widmung) ? DATA.extras.widmung.price : 0) +
      ((s.extra_premium_papier && DATA.extras && DATA.extras.premium_papier) ? DATA.extras.premium_papier.price : 0);

    const books    = DATA.upsell_books || [];
    const selected = getUpsellSelected();

    // Bündel = Hauptbuch + Zusatzbücher — exakt wie die Shopify Discount Function:
    // absteigend nach Stückpreis, teuerstes bleibt voll, 2. −25 %, ab dem 3. −40 %.
    // (Stabiler Tie-Break über idx → bei gleichem Preis bleibt das Hauptbuch voll.)
    const lines = [{ key: '__main__', price: mainPrice }];
    let upsellFull = 0;
    selected.forEach(function (handle) {
      const b = books.find(function (x) { return x.handle === handle; });
      if (!b) return;
      const v = pickUpsellVariant(b, s.binding);
      const price = v ? v.price : b.price;
      upsellFull += price;
      lines.push({ key: handle, price: price });
    });

    const ranked = lines
      .map(function (l, idx) { return { key: l.key, price: l.price, idx: idx }; })
      .sort(function (a, b) { return (b.price - a.price) || (a.idx - b.idx); });

    let savings = 0, bundleNet = 0;
    const tierByHandle = {};
    ranked.forEach(function (l, rank) {
      const pct = rank === 0 ? 0 : (rank === 1 ? 0.25 : 0.40);
      const net = l.price * (1 - pct);
      bundleNet += net;
      savings   += l.price * pct;
      if (l.key !== '__main__') {
        tierByHandle[l.key] = { full: l.price, net: net, pct: Math.round(pct * 100) };
      }
    });

    const currentTotal = mainPrice + extrasTotal;   // "aktuell": dein Buch + Extras (Hauptbuch voll)
    const upsellNet    = bundleNet - mainPrice;      // Netto-Anteil der Zusatzbücher (Rabatt gebündelt)
    const grandTotal   = extrasTotal + bundleNet;    // neuer Gesamtpreis == Checkout
    return {
      count: selected.length, mainPrice, extrasTotal, currentTotal,
      upsellFull, upsellNet, savings, grandTotal, tierByHandle
    };
  }

  // Gesamtpreis inkl. Bündel — für den Footer-CTA (auf Step < 7 == calcTotal).
  function calcGrandTotal() { return calcBundle().grandTotal; }

  // Grüne Deal-Leiste, zwei Zustände: 0 gewählt = Angebot (drängt), >=1 = Ersparnis.
  function renderUpsellBar(bundle) {
    const box = root.querySelector('[data-wp-upsell-bar]');
    if (!box) return;
    box.hidden = false;
    if (!bundle || bundle.count <= 0) {
      box.className = 'wp-upsell-bar';
      box.innerHTML =
        '<span class="wp-upsell-bar__deal">Mehr Bücher, mehr sparen</span>' +
        '<span class="wp-upsell-bar__terms">2. Buch <b>−25 %</b> · ab dem 3. <b>−40 %</b></span>';
      return;
    }
    box.className = 'wp-upsell-bar is-active';
    const fullTotal = bundle.currentTotal + bundle.upsellFull;
    box.innerHTML =
      '<span class="wp-upsell-bar__save">Du sparst <b>' + fmtMoney(bundle.savings) + '</b></span>' +
      '<span class="wp-upsell-bar__total"><s>' + fmtMoney(fullTotal) + '</s> <b>' + fmtMoney(bundle.grandTotal) + '</b></span>';
  }

  const UPSELL_CHECK_SVG =
    '<svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 10 8 14 16 5"></polyline></svg>';

  function renderStep7Upsell() {
    const container = root.querySelector('[data-wp-upsell]');
    if (!container) return;
    const books = DATA.upsell_books || [];
    const s = state.load();

    const nameEl = root.querySelector('[data-wp-upsell-name]');
    if (nameEl) nameEl.textContent = (s.name && s.name.trim()) ? s.name.trim() : 'dein Kind';

    if (!books.length) {
      container.innerHTML = '';
      const teaser = root.querySelector('[data-wp-upsell-teaser]');
      if (teaser) teaser.hidden = true;
      return;
    }

    const selected = getUpsellSelected();
    const binding = s.binding || 'HC';
    const bundle = calcBundle();

    container.innerHTML = books.map(function (b) {
      const v = pickUpsellVariant(b, binding);
      const price = v ? v.price : b.price;
      const isSel = selected.indexOf(b.handle) !== -1;
      const tier  = bundle.tierByHandle[b.handle];
      const priceHtml = (isSel && tier)
        ? '<b>' + fmtMoney(tier.net) + '</b>' + (tier.pct > 0 ? ' <em class="wp-upsell2-card__off">−' + tier.pct + ' %</em>' : '')
        : fmtMoney(price);

      return '' +
        '<button type="button" class="wp-upsell2-card' + (isSel ? ' is-selected' : '') + '" data-wp-upsell-book="' + escapeHtml(b.handle) + '" aria-pressed="' + isSel + '">' +
          '<span class="wp-upsell2-card__media">' + (b.image ? '<img src="' + b.image + '" alt="' + escapeHtml(b.title) + '" loading="lazy">' : '') + '<span class="wp-upsell2-card__toggle" aria-hidden="true">' + (isSel ? UPSELL_CHECK_SVG : '+') + '</span></span>' +
          '<span class="wp-upsell2-card__body"><span class="wp-upsell2-card__title">' + escapeHtml(b.title) + '</span><span class="wp-upsell2-card__price">' + priceHtml + '</span></span>' +
        '</button>';
    }).join('');

    if (!container.dataset.wpUpsellBound) {
      container.dataset.wpUpsellBound = '1';
      container.addEventListener('click', function (e) {
        const card = e.target.closest('[data-wp-upsell-book]');
        if (!card) return;
        const handle = card.getAttribute('data-wp-upsell-book');
        let sel = getUpsellSelected();
        sel = (sel.indexOf(handle) !== -1) ? sel.filter(function (h) { return h !== handle; }) : sel.concat(handle);
        state.set('upsell_selected', sel);
        renderStep7Upsell();
        updateNextButtonPrice();
      });
    }

    renderUpsellBar(bundle);
  }

  function buildCartPayload(s) {
    const bindingSku = getBindingSku(s.binding);

    // Haar-/Augenfarbe wandern als Klarname UND als Hex-Code mit —
    // n8n/NanoBanana nutzt den eindeutigen Hex, der Klarname bleibt
    // nur für die lesbare Bestellübersicht im Shopify-Admin.
    const mainProperties = {
      '_name':             s.name || '',
      '_gender':           s.gender || '',
      '_hair':             s.hair || '',
      '_hair_hex':         HAIR_COLORS[s.hair] || '',
      '_eyes':             s.eyes || '',
      '_eyes_hex':         EYE_COLORS[s.eyes] || '',
      '_photo_url':        s.photo_url || '',
      '_photo_public_id':  s.photo_public_id || '',
      '_binding':          s.binding || '',
      '_bundle':           'funnel'
    };

    const items = [{
      id: s.binding_variant_id,
      quantity: 1,
      properties: mainProperties
    }];

    if (s.extra_widmung && DATA.extras && DATA.extras.widmung && DATA.extras.widmung.variant_id) {
      items.push({
        id: DATA.extras.widmung.variant_id,
        quantity: 1,
        properties: {
          '_widmung_text':   (s.extra_widmung_text || '').trim(),
          '_zugehoerig_zu':  bindingSku
        }
      });
    }

    if (s.extra_premium_papier && DATA.extras && DATA.extras.premium_papier && DATA.extras.premium_papier.variant_id) {
      items.push({
        id: DATA.extras.premium_papier.variant_id,
        quantity: 1,
        properties: {
          '_zugehoerig_zu': bindingSku
        }
      });
    }

    // Upsell-Bücher: gleiche Personalisierung + gleiche Bindung, Marker fürs Bündel
    const selectedUp = getUpsellSelected();
    const upBooks = DATA.upsell_books || [];
    selectedUp.forEach(function (handle) {
      const book = upBooks.find(function (b) { return b.handle === handle; });
      if (!book) return;
      const uv = pickUpsellVariant(book, s.binding);
      if (!uv || !uv.id) return;
      items.push({ id: uv.id, quantity: 1, properties: Object.assign({}, mainProperties, { '_zugehoerig_zu': bindingSku }) });
    });

    return { items };
  }

  function getBindingSku(code) {
    if (!code) return '';
    const map = getBindingVariantMap();
    // Fällt auf den Binding-Code zurück, falls die Variante keine SKU hat.
    return (map[code] && map[code].sku) || code;
  }

  function setSubmitState(stateName, payload = {}) {
    const btn      = root.querySelector('[data-wp-action="next"]');
    const errBox   = root.querySelector('[data-wp-submit-error]');
    const errMsg   = root.querySelector('[data-wp-submit-error-msg]');

    if (stateName === 'submitting') {
      if (btn) {
        btn.disabled = true;
        btn.classList.add('is-submitting');
      }
      if (errBox) errBox.hidden = true;
      return;
    }

    if (stateName === 'error') {
      if (btn) {
        btn.classList.remove('is-submitting');
        btn.disabled = false;
      }
      if (errBox) errBox.hidden = false;
      if (errMsg && payload.message) errMsg.textContent = payload.message;
      return;
    }

    // idle
    if (btn) btn.classList.remove('is-submitting');
    if (errBox) errBox.hidden = true;
  }

  let submitInFlight = false;

  async function submitOrder() {
    if (submitInFlight) return; // Doppelklick-Guard
    const s = state.load();

    if (!s.binding_variant_id) {
      setSubmitState('error', { message: 'Bitte vorher eine Bindung wählen (Schritt 5).' });
      return;
    }
    if (!s.photo_url) {
      setSubmitState('error', { message: 'Es fehlt noch das Foto (Schritt 2).' });
      return;
    }
    if (s.extra_widmung === true && !(s.extra_widmung_text && s.extra_widmung_text.trim())) {
      setSubmitState('error', { message: 'Bitte den Widmungs-Text ergänzen oder die Widmung wieder ausschalten (Schritt 6).' });
      return;
    }

    const payload = buildCartPayload(s);
    console.log('[funnel] Submit-Payload:', payload);

    submitInFlight = true;
    setSubmitState('submitting');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        let msg = `Konnte nicht in den Warenkorb (Status ${res.status}).`;
        try {
          const errJson = await res.json();
          if (errJson && errJson.description) msg = errJson.description;
          else if (errJson && errJson.message) msg = errJson.message;
        } catch (_) {}
        throw new Error(msg);
      }

      console.log('[funnel] Cart-Add erfolgreich');
      state.clear();
      // Side-Cart-Drawer im DOM → Funnel schließen und Drawer öffnen.
      // Sonst klassischer Redirect auf die Warenkorb-Seite.
      var sideCart = document.querySelector('[data-wp-cart]');
      if (sideCart) {
        submitInFlight = false;
        setSubmitState('idle');
        closeFunnel();
        window.dispatchEvent(new CustomEvent('wp:cart:added'));
      } else {
        window.location.href = '/cart';
      }
    } catch (err) {
      console.error('[funnel] Submit-Fehler:', err);
      submitInFlight = false;
      setSubmitState('error', {
        message: err.message || 'Konnte die Bestellung nicht übernehmen. Bitte noch einmal versuchen.'
      });
    }
  }

  // ---------- Helpers ----------
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(str) {
    // Für data-Attribute (kein <, >, &, ", ')
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function cssEscape(str) {
    // Für querySelector-Attribute mit Sonderzeichen (Umlaute sind ok, aber " escaped)
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(str);
    }
    return String(str).replace(/"/g, '\\"');
  }

  // ---------- Globale Event-Handler ----------
  function bindEvents() {
    root.addEventListener('input', (e) => {
      const extraText = e.target.closest('[data-wp-extra-text]');
      if (extraText) {
        const key = extraText.dataset.wpExtraText;
        const val = extraText.value;
        state.set(`extra_${key}_text`, val);
        const counter = root.querySelector(`[data-wp-extra-counter="${key}"]`);
        if (counter) counter.textContent = String(val.length);
        validateCurrentStep();
        return;
      }

      const field = e.target.closest('[data-wp-field]');
      if (!field) return;
      if (field.type === 'checkbox') return; // Checkboxen laufen über 'change'
      state.set(field.dataset.wpField, field.value);
      if (field.dataset.wpField === 'name') invalidatePreview(); // Name geändert → Vorschau neu erzeugen
      validateCurrentStep();
    });

    root.addEventListener('change', (e) => {
      const extraToggle = e.target.closest('[data-wp-extra-toggle]');
      if (extraToggle) {
        const key = extraToggle.dataset.wpExtraToggle;
        toggleExtra(key, extraToggle.checked);
        return;
      }

      const field = e.target.closest('[data-wp-field]');
      if (!field || field.type !== 'checkbox') return;
      if (field.dataset.wpField === 'photo_consent') {
        handleConsentChange(field); // Rechte-Bestätigung → unsichtbarer Bot-Check
        return;
      }
      state.set(field.dataset.wpField, field.checked);
      validateCurrentStep();
    });

    root.addEventListener('click', (e) => {
      const opt = e.target.closest('[data-wp-option]');
      if (opt) {
        const key = opt.dataset.wpOption;
        const value = opt.dataset.wpValue;
        root.querySelectorAll(`[data-wp-option="${key}"]`).forEach(el => el.classList.remove('is-selected'));
        opt.classList.add('is-selected');
        state.set(key, value);
        invalidatePreview(); // Geschlecht/Haar/Augen geändert → Vorschau neu erzeugen
        validateCurrentStep();
        return;
      }

      const bindingCard = e.target.closest('[data-wp-binding]');
      if (bindingCard) {
        const code = bindingCard.dataset.wpBinding;
        const variantId = parseInt(bindingCard.dataset.wpVariantId, 10);
        const price = parseInt(bindingCard.dataset.wpPrice, 10);
        selectBinding(code, variantId, price);
        return;
      }

      const action = e.target.closest('[data-wp-action]');
      if (action) {
        const a = action.dataset.wpAction;
        if (a === 'next' && !action.disabled) nextStep();
        if (a === 'back') prevStep();
        if (a === 'replace-photo' || a === 'retry-photo') openFilePicker();
        if (a === 'retry-preview') retryPreview();
      }
    });

    window.addEventListener('popstate', () => {
      const step = getStepFromURL();
      if (step !== currentStep) showStep(step);
    });
  }

  // ---------- Init ----------
  cacheUploadEls();
  cacheLoadingEls();
  renderOptions();
  renderBindingOptions();
  renderExtras();
  renderStep7Upsell();
  restoreUI();
  bindEvents();
  bindUploadEvents();
  showStep(currentStep);

  // Debug-Hook
  window.__wpFunnel = {
    state, DATA, showStep, nextStep, prevStep,
    setUploadState, uploadToCloudinary,
    captchaRequired, freshTurnstileToken, executeBotCheck, initTurnstile,
    startLoadingAnimation, requestPreview, retryPreview,
    renderBookPreview, renderBookMockup, renderLegacyPreview,
    renderBindingOptions, renderExtras, renderOptions,
    selectBinding, toggleExtra, ensureBindingDefault,
    calcTotal, updateNextButtonPrice, formatEUR,
    BINDINGS, HAIR_COLORS, EYE_COLORS, GENDER_EMOJI,
    renderStep7Upsell,
    buildCartPayload: () => buildCartPayload(state.load()),
    submitOrder, setSubmitState,
    UPSELL_MOCKS
  };
})();